import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import {
	Dialog,
	Button,
	CircularProgress,
	TextField,
	Typography,
	IconButton,
	InputAdornment,
	ButtonBase
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import LabelOutlinedIcon from "@material-ui/icons/LabelOutlined";
import HeadsetMicOutlinedIcon from "@material-ui/icons/HeadsetMicOutlined";
import CheckCircleRoundedIcon from "@material-ui/icons/CheckCircleRounded";
import QrCodeIcon from "@material-ui/icons/CropFree";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import {
	VERDE,
	modalPapel,
	modalHero,
	modalHeroLinha,
	modalHeroIcone,
	modalHeroEtiqueta,
	modalHeroTitulo,
	modalHeroSub,
	modalFechar,
	modalCorpo,
	modalSecao,
	modalSecaoTopo,
	modalSecaoIcone,
	modalSecaoTitulo,
	modalSecaoSub,
	modalRodape,
	campoNavy,
	botaoPrimario,
	botaoSecundario
} from "../../utils/estiloNavy";

const useStyles = makeStyles(theme => {
	const escuro = theme.palette.type === "dark";
	const borda = escuro ? "rgba(255,255,255,0.10)" : "#e5ebf2";
	return {
		papel: modalPapel(theme, 520),
		hero: modalHero(theme),
		heroLinha: modalHeroLinha,
		heroIcone: modalHeroIcone,
		heroEtiqueta: modalHeroEtiqueta,
		heroTitulo: modalHeroTitulo,
		heroSub: modalHeroSub,
		fechar: { ...modalFechar, marginLeft: "auto", alignSelf: "flex-start" },
		corpo: modalCorpo(theme),
		secao: modalSecao(theme),
		secaoTopo: modalSecaoTopo,
		secaoIcone: modalSecaoIcone,
		secaoTitulo: modalSecaoTitulo(theme),
		secaoSub: modalSecaoSub(theme),
		campo: campoNavy(theme),
		rodape: { ...modalRodape(theme), justifyContent: "flex-end" },
		btnPrimario: botaoPrimario,
		btnSecundario: botaoSecundario(theme),

		opcoes: { display: "flex", flexDirection: "column", gap: 8 },
		opcao: {
			width: "100%",
			display: "flex",
			alignItems: "center",
			gap: 12,
			padding: "10px 12px",
			borderRadius: 14,
			textAlign: "left",
			justifyContent: "flex-start",
			border: `1px solid ${borda}`,
			background: escuro ? "rgba(255,255,255,0.02)" : "#fff",
			transition: "border-color .15s, box-shadow .15s, background-color .15s",
			"&:hover": {
				borderColor: "rgba(30,152,85,0.5)",
				boxShadow: "0 4px 14px -8px rgba(30,152,85,0.6)",
			},
		},
		opcaoAtiva: {
			borderColor: VERDE,
			background: escuro ? "rgba(30,152,85,0.12)" : "#f0fbf4",
			boxShadow: "0 0 0 3px rgba(30,152,85,0.12)",
		},
		opcaoIcone: {
			width: 34,
			height: 34,
			borderRadius: 10,
			flexShrink: 0,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			"& svg": { fontSize: 18 },
		},
		opcaoNome: { fontSize: "0.9rem", fontWeight: 700, color: theme.palette.text.primary },
		opcaoDetalhe: { fontSize: "0.74rem", color: theme.palette.text.secondary },
		pilula: {
			fontSize: 10.5,
			fontWeight: 800,
			borderRadius: 999,
			padding: "2px 8px",
			whiteSpace: "nowrap",
		},
		marca: { color: VERDE, fontSize: 20, marginLeft: 4 },
		dica: {
			display: "flex",
			gap: 10,
			alignItems: "flex-start",
			fontSize: "0.78rem",
			color: theme.palette.text.secondary,
			padding: "10px 12px",
			borderRadius: 12,
			background: escuro ? "rgba(30,152,85,0.08)" : "rgba(30,152,85,0.06)",
			border: `1px dashed ${escuro ? "rgba(52,211,153,0.3)" : "rgba(30,152,85,0.3)"}`,
			"& svg": { fontSize: 18, color: VERDE, flexShrink: 0, marginTop: 1 },
		},
	};
});

const SessionSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Nome muito curto")
		.max(50, "Nome muito longo")
		.required("O nome da conexão é obrigatório"),
});

const WhatsAppModal = ({ open, onClose, whatsAppId }) => {
	const classes = useStyles();
	const initialState = {
		name: "",
		chatbotId: "",
	};
	const [whatsApp, setWhatsApp] = useState(initialState);
	const [chatbots, setChatbots] = useState([]);
	const [conexoes, setConexoes] = useState([]);

	useEffect(() => {
		if (!open) return;
		const carregar = async () => {
			try {
				const [{ data: fluxos }, { data: lista }] = await Promise.all([
					api.get("/chatbots"),
					api.get("/whatsapp"),
				]);
				setChatbots(fluxos || []);
				setConexoes(lista || []);
			} catch (err) {
				console.error("Erro ao carregar fluxos:", err);
			}
		};
		carregar();
	}, [open]);

	useEffect(() => {
		const fetchSession = async () => {
			if (!whatsAppId) {
				// Numero NOVO ja entra no fluxo principal (o que tem mais numeros
				// vinculados). O adm pode trocar ou deixar sem fluxo aqui mesmo.
				let fluxoPrincipal = "";
				try {
					const [{ data: lista }, { data: fluxos }] = await Promise.all([
						api.get("/whatsapp"),
						api.get("/chatbots"),
					]);
					const contagem = {};
					(lista || []).forEach(w => {
						if (w.chatbotId) contagem[w.chatbotId] = (contagem[w.chatbotId] || 0) + 1;
					});
					const ativos = (fluxos || []).filter(f => f.active !== false);
					ativos.sort((a, b) => (contagem[b.id] || 0) - (contagem[a.id] || 0));
					if (ativos.length && (contagem[ativos[0].id] || ativos.length === 1)) {
						fluxoPrincipal = ativos[0].id;
					}
				} catch (e) {
					// sem sugestao, segue vazio
				}
				setWhatsApp({ ...initialState, chatbotId: fluxoPrincipal });
				return;
			}

			try {
				const { data } = await api.get(`/whatsapp/${whatsAppId}`);
				setWhatsApp({
					name: data.name || "",
					chatbotId: data.chatbotId || "",
				});
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, [whatsAppId]);

	const handleSaveWhatsApp = async values => {
		const whatsappData = {
			name: values.name,
			chatbotId: values.chatbotId ? parseInt(values.chatbotId) : null,
		};

		try {
			if (whatsAppId) {
				await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
			} else {
				await api.post("/whatsapp", whatsappData);
			}
			toast.success(i18n.t("whatsappModal.success"));
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	const handleClose = () => {
		onClose();
		setWhatsApp(initialState);
	};

	const noAr = id =>
		conexoes.filter(
			w => w.chatbotId === id && w.ativa !== false && w.status === "CONNECTED"
		).length;

	const Opcao = ({ ativo, onClick, icone, corIcone, fundoIcone, nome, detalhe, pilula }) => (
		<ButtonBase
			className={`${classes.opcao} ${ativo ? classes.opcaoAtiva : ""}`}
			onClick={onClick}
		>
			<span className={classes.opcaoIcone} style={{ color: corIcone, background: fundoIcone }}>
				{icone}
			</span>
			<span style={{ flex: 1, minWidth: 0 }}>
				<div className={classes.opcaoNome}>{nome}</div>
				<div className={classes.opcaoDetalhe}>{detalhe}</div>
			</span>
			{pilula}
			{ativo && <CheckCircleRoundedIcon className={classes.marca} />}
		</ButtonBase>
	);

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth={false}
			PaperProps={{ className: classes.papel }}
		>
			<div className={classes.hero}>
				<div className={classes.heroLinha}>
					<div className={classes.heroIcone}>
						<WhatsAppIcon />
					</div>
					<div style={{ minWidth: 0 }}>
						<div className={classes.heroEtiqueta}>Conexões</div>
						<div className={classes.heroTitulo}>
							{whatsAppId ? "Editar WhatsApp" : "Adicionar WhatsApp"}
						</div>
						<div className={classes.heroSub}>
							{whatsAppId
								? "Mude o nome ou o fluxo que atende por este número."
								: "Dê um nome e escolha o fluxo. O QR Code aparece em seguida."}
						</div>
					</div>
					<IconButton size="small" onClick={handleClose} className={classes.fechar}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</div>
			</div>

			<Formik
				initialValues={whatsApp}
				enableReinitialize={true}
				validationSchema={SessionSchema}
				onSubmit={(values, actions) => {
					setTimeout(() => {
						handleSaveWhatsApp(values);
						actions.setSubmitting(false);
					}, 200);
				}}
			>
				{({ values, touched, errors, isSubmitting, setFieldValue }) => (
					<Form style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
						<div className={classes.corpo}>
							<div className={classes.secao}>
								<div className={classes.secaoTopo}>
									<span className={classes.secaoIcone}><LabelOutlinedIcon /></span>
									<div>
										<div className={classes.secaoTitulo}>Identificação</div>
										<div className={classes.secaoSub}>Como este número aparece no sistema.</div>
									</div>
								</div>
								<Field
									as={TextField}
									label="Nome da conexão"
									placeholder="Ex: Consig - 1008 - cel 17"
									autoFocus
									fullWidth
									name="name"
									error={touched.name && Boolean(errors.name)}
									helperText={touched.name && errors.name}
									variant="outlined"
									size="small"
									className={classes.campo}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<WhatsAppIcon style={{ fontSize: 18, color: VERDE }} />
											</InputAdornment>
										),
									}}
								/>
							</div>

							<div className={classes.secao} style={{ animationDelay: "60ms" }}>
								<div className={classes.secaoTopo}>
									<span className={classes.secaoIcone}><AccountTreeIcon /></span>
									<div>
										<div className={classes.secaoTitulo}>Fluxo visual</div>
										<div className={classes.secaoSub}>Quem atende os clientes que chegam por este número.</div>
									</div>
								</div>
								<div className={classes.opcoes}>
									{chatbots.map(cb => {
										const qtd = noAr(cb.id);
										return (
											<Opcao
												key={cb.id}
												ativo={String(values.chatbotId) === String(cb.id)}
												onClick={() => setFieldValue("chatbotId", cb.id)}
												icone={<AccountTreeIcon />}
												corIcone={VERDE}
												fundoIcone="rgba(30,152,85,0.12)"
												nome={cb.name}
												detalhe={cb.active === false ? "Fluxo pausado" : "Fluxo automático"}
												pilula={
													<span
														className={classes.pilula}
														style={{
															color: qtd ? VERDE : "#94a3b8",
															background: qtd ? "rgba(30,152,85,0.12)" : "rgba(148,163,184,0.15)",
														}}
													>
														{qtd} no ar
													</span>
												}
											/>
										);
									})}
									<Opcao
										ativo={!values.chatbotId}
										onClick={() => setFieldValue("chatbotId", "")}
										icone={<HeadsetMicOutlinedIcon />}
										corIcone="#64748b"
										fundoIcone="rgba(100,116,139,0.12)"
										nome="Nenhum fluxo"
										detalhe="Cai direto no atendimento humano"
									/>
								</div>
							</div>

							{!whatsAppId && (
								<div className={classes.dica}>
									<QrCodeIcon />
									<span>
										Depois de adicionar, clique em <b>QR Code</b> no cartão da conexão e leia
										com o celular em <b>Aparelhos conectados</b>.
									</span>
								</div>
							)}
						</div>

						<div className={classes.rodape}>
							<Button onClick={handleClose} disabled={isSubmitting} className={classes.btnSecundario}>
								Cancelar
							</Button>
							<Button type="submit" disabled={isSubmitting} className={classes.btnPrimario}>
								{isSubmitting && (
									<CircularProgress size={16} style={{ color: "#fff", marginRight: 8 }} />
								)}
								{whatsAppId ? "Salvar alterações" : "Adicionar WhatsApp"}
							</Button>
						</div>
					</Form>
				)}
			</Formik>
		</Dialog>
	);
};

export default React.memo(WhatsAppModal);
